/*
 * Copyright (c) Nexign, JSC, 2020.
 */
/**
 * DatePicker Header - Single implementation
 * Year and month selectors are joined to one
 *
 * @author Yakov Gushchin
 * @version 1.10.0
 * @since 1.10.0
 */

import { useEffect, useState } from 'react';

import { icons } from '../../../../../assets';
import useDatePickerHeader from '../../hooks/Header/useDatePickerHeader';
import { DatePickerHeaderTitleSingleStyled } from '../../styled/Header/DatePickerHeaderSingleStyled';
import {
    DatePickerHeaderLineStyled,
    DatePickerHeaderSelectorArrowStyled,
    DatePickerHeaderSelectorStyled,
    DatePickerHeaderStyled,
    DatePickerHeaderWeekCellStyled,
    DatePickerHeaderWeekStyled,
} from '../../styled/Header/DatePickerHeaderStyled';
import { IDatePickerHeaderProps } from '../../types/datePickerTypes';

type TDatePickerHeaderSingle = IDatePickerHeaderProps;

const DatePickerHeaderSingle = (props: TDatePickerHeaderSingle) => {

    const {
        period,
        locale,
        maxDate,
        minDate,
        periodChangeHandler,
    } = props;

    /*
    * -------------------------------------------
    *       Hooks: Component logic
    * -------------------------------------------
    */

    const {
        isArrowDisabled,
        onArrowClick,
    } = useDatePickerHeader({
        period,
        maxDate,
        minDate,
        periodChangeHandler: periodChangeHandler,
    });

    const [ label, setLabel ] = useState('');

    useEffect(() => {
        const month = period.month();
        const year = period.year();

        setLabel(`${locale.monthNames[month]} ${year}`)
    }, [ locale, period ])


    /*
    * -------------------------------------------
    *               Handlers
    * -------------------------------------------
    */

    const onLeftArrowClickHandler = () => {
        onArrowClick('month', 'decrease');
    }


    const onRightArrowClickHandler = () => {
        onArrowClick('month', 'increase');
    }

    /*
    * -------------------------------------------
    *           Render component
    * -------------------------------------------
    */

    const renderWeekTmpl = <DatePickerHeaderWeekStyled>
        {locale.weekNames.map((week: string, key: number) =>
            // @ts-ignore
            <DatePickerHeaderWeekCellStyled key={key}>{week}</DatePickerHeaderWeekCellStyled>,
        )}
    </DatePickerHeaderWeekStyled>;

    return (
        <DatePickerHeaderStyled>
            <DatePickerHeaderSelectorStyled>
                <DatePickerHeaderSelectorArrowStyled
                    icon={icons.arrowListLeftSmall}
                    isDisabled={isArrowDisabled('month', 'decrease')}
                    side="left"
                    onClick={onLeftArrowClickHandler}
                />
                <DatePickerHeaderTitleSingleStyled>{label}</DatePickerHeaderTitleSingleStyled>
                <DatePickerHeaderSelectorArrowStyled
                    icon={icons.arrowListLeftSmall}
                    isDisabled={isArrowDisabled('month', 'increase')}
                    side="right"
                    onClick={onRightArrowClickHandler}
                />
            </DatePickerHeaderSelectorStyled>
            {renderWeekTmpl}
            <DatePickerHeaderLineStyled/>
        </DatePickerHeaderStyled>
    );
};

export default DatePickerHeaderSingle;
